<template>
  <div class="w-full">
    <div class="flex items-start justify-between">
      <div>
        <h1 class="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white tracking-tight">{{ t('agentEvals.title', 'Agent Evals') }}</h1>
        <p class="text-slate-600 dark:text-slate-400 mt-2">{{ t('agentEvals.subtitle', 'Create eval suites for agents and run automated checks with a judge prompt.') }}</p>
      </div>
      <button
        type="button"
        class="btn"
        @click="openCreate = true"
      >
        {{ t('agentEvals.createEval', 'Create Eval') }}
      </button>
    </div>

    <div class="mt-8">
      <div class="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm">
        <div class="px-6 py-5 border-b border-slate-200 dark:border-slate-700">
          <div class="text-lg font-bold text-slate-900 dark:text-white">{{ t('agentEvals.evals', 'Evals') }}</div>
          <div class="text-xs text-slate-500 dark:text-slate-400 mt-1">{{ t('agentEvals.selectEvalHint', 'Select an eval to edit or run') }}</div>
        </div>
        <div class="p-4">
          <div v-if="loading === true" class="-m-3">
            <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
              <div v-for="n in 6" :key="`eval-skel-${n}`" class="p-3">
                <div class="w-full rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-5 shadow-sm">
                  <div class="flex items-start justify-between">
                    <div class="min-w-0 flex-1">
                      <div class="h-5 w-2/3 skeleton"></div>
                      <div class="mt-3 h-3 w-3/4 skeleton"></div>
                    </div>
                    <div class="flex flex-col items-end" style="margin-inline-start: 0.75rem">
                      <div class="h-6 w-20 skeleton"></div>
                      <div class="mt-3 h-7 w-12 skeleton"></div>
                    </div>
                  </div>
                  <div class="mt-5 flex items-center justify-between">
                    <div class="h-3 w-1/3 skeleton"></div>
                    <div class="h-6 w-20 skeleton"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-else-if="evals.length === 0" class="text-sm text-slate-500 dark:text-slate-400 py-4">{{ t('agentEvals.noEvalsYet', 'No evals yet') }}</div>
          <div v-else class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 -m-3">
            <div v-for="e in evals" :key="e.evalId" class="p-3">
              <button
                type="button"
                class="w-full rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50 hover:bg-white dark:bg-slate-800/40 dark:hover:bg-slate-800/70 transition-colors p-5 shadow-sm"
                style="text-align: start"
                @click="openEval(e.evalId)"
              >
                <div class="flex items-start justify-between">
                  <div class="min-w-0">
                    <div class="text-base font-bold text-slate-900 dark:text-white truncate">{{ e.evalName }}</div>
                    <div class="text-xs text-slate-500 dark:text-slate-400 mt-1 truncate">{{ t('agentEvals.agentLabel', 'Agent:') }} {{ e.agentId }}</div>
                  </div>
                  <div class="flex flex-col items-end space-y-2" style="margin-inline-start: 0.75rem">
                    <div class="text-xs font-semibold text-slate-600 dark:text-slate-300 bg-white/80 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700 rounded-lg px-2 py-1">
                      {{ e.testsCount }} {{ t('agentEvals.testsLabel', 'tests') }}
                    </div>
                    <button
                      type="button"
                      class="btn btn-sm"
                      @click.stop="emit('openRun', e.evalId)"
                    >
                      {{ t('agentEvals.run', 'Run') }}
                    </button>
                  </div>
                </div>
                <div class="mt-3 flex items-center justify-between">
                  <div class="text-xs text-slate-500 dark:text-slate-400">
                    <span v-if="e.lastRunAt !== null && e.lastRunAt !== undefined">{{ t('agentEvals.lastRunLabel', 'Last run:') }} {{ formatDate(e.lastRunAt) }}</span>
                    <span v-else>{{ t('agentEvals.notRunYet', 'Not run yet') }}</span>
                  </div>
                  <div v-if="e.overallPass !== undefined && e.overallPass !== null" style="margin-inline-start: 0.75rem">
                    <span v-if="e.overallPass === true" class="text-xs font-bold text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-900 px-2.5 py-1 rounded-full">{{ t('agentEvals.passed', 'Passed') }}</span>
                    <span v-else class="text-xs font-bold text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900 px-2.5 py-1 rounded-full">{{ t('agentEvals.notPassed', 'Not passed') }}</span>
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="errorText !== ''" class="mt-6 rounded-2xl border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-900/20 p-5 text-sm text-red-700 dark:text-red-300">
      {{ errorText }}
    </div>

    <div v-if="openCreate === true" class="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div class="absolute inset-0 bg-black/50" @click="openCreate = false"></div>
      <div class="relative w-full max-w-xl rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-2xl p-6">
        <div class="text-lg font-bold text-slate-900 dark:text-white">Create Agent Eval</div>
        <div class="mt-5 space-y-4">
          <div class="space-y-2">
            <label class="text-sm font-semibold text-slate-700 dark:text-slate-300">Eval Name</label>
            <input
              type="text"
              class="w-full rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              v-model="createEvalName"
            />
          </div>
          <div class="space-y-2">
            <label class="text-sm font-semibold text-slate-700 dark:text-slate-300">Agent</label>
            <MultiSelect
              v-model="createAgentId"
              :options="agents"
              placeholder="Select agent"
              id-field="agentId"
              label-field="agentName"
              :max-selections="1"
              buttonClass="!rounded-xl !border-2 !border-slate-200 dark:!border-slate-700 !bg-white dark:!bg-slate-800 !text-slate-900 dark:!text-white !px-4 !py-3 !h-auto !min-h-[50px] focus:!ring-2 focus:!ring-blue-500 focus:!border-blue-500"
            />
          </div>
        </div>
        <div class="mt-6 flex items-center justify-end">
          <button
            type="button"
            class="btn"
            @click="openCreate = false"
          >
            {{ t('common.cancel', 'Cancel') }}
          </button>
          <button
            type="button"
            class="btn"
            style="margin-inline-start: 0.75rem"
            @click="createEval"
            :disabled="creatingEval === true"
          >
            {{ creatingEval === true ? t('agentEvals.creating', 'Creating...') : t('agentEvals.create', 'Create') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { globalState } from '@/globalState';
import { useI18n } from '@/i18n.js';
import MultiSelect from '@/components/MultiSelect.vue';

const { t } = useI18n();

const props = defineProps({
  agents: { type: Array, required: true },
  evals: { type: Array, required: true },
  loading: { type: Boolean, required: true },
  errorText: { type: String, required: true }
});

const emit = defineEmits(['openEval', 'openRun', 'createdEval', 'setError']);

const openCreate = ref(false);
const createEvalName = ref('');
const createAgentId = ref([]);
const creatingEval = ref(false);

const agents = computed(() => Array.isArray(props.agents) === true ? props.agents : []);
const evals = computed(() => Array.isArray(props.evals) === true ? props.evals : []);

function getAuthToken() {
  const authToken = document.cookie.split('; ').find(row => row.startsWith('authToken=') != false)?.split('=')[1];
  const jwtToken = document.cookie.split('; ').find(row => row.startsWith('jwtToken=') != false)?.split('=')[1];
  const token = authToken !== undefined && authToken !== null && authToken !== '' ? authToken : jwtToken;
  if (token === undefined || token === null) {
    return '';
  }
  return token;
}

function getAuthHeaders() {
  const token = getAuthToken();
  const headers = {
    'Content-Type': 'application/json'
  };
  if (token !== '' && token !== undefined && token !== null) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

function openEval(evalId) {
  emit('openEval', evalId);
}

function formatDate(value) {
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime()) === true) {
      return '';
    }
    return d.toLocaleString();
  } catch (e) {
    return '';
  }
}

async function createEval() {
  if (creatingEval.value === true) {
    return;
  }
  const name = typeof createEvalName.value === 'string' ? createEvalName.value.trim() : '';
  const agentId = Array.isArray(createAgentId.value) === true && createAgentId.value.length > 0 ? String(createAgentId.value[0]).trim() : '';
  if (name.length === 0 || agentId.length === 0) {
    emit('setError', 'Please fill all fields');
    return;
  }
  creatingEval.value = true;
  try {
    const response = await fetch(`${globalState.apiEndpoint}/api/agentEvals`, {
      method: 'POST',
      headers: getAuthHeaders(),
      credentials: 'include',
      body: JSON.stringify({ evalName: name, agentId })
    });
    if (response.ok === true) {
      const created = await response.json();
      openCreate.value = false;
      createEvalName.value = '';
      createAgentId.value = [];
      emit('createdEval', created);
    } else {
      emit('setError', 'Failed to create eval');
    }
  } catch (e) {
    emit('setError', 'Failed to create eval');
  } finally {
    creatingEval.value = false;
  }
}
</script>


